import { useInjection } from 'inversify-react';
import { constant } from 'lodash/fp';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useState } from 'react';
import { doNothing, doNothingAsync } from '../../../helpers/function.helpers';
import { IAnnotationUiStore } from '../../annotation/annotationUi.store';
import { Image } from '../../annotation/components/Image';
import { SegmentationsLayer } from '../../annotation/components/SegmentationsLayer';
import { DrawingContainer } from '../../annotation/containers/Drawing.container';
import { isAttributeQuestion } from '../../annotation/question.model';
import { VerticalMenuItem } from '../../annotation/submodules/segmentations/components/verticalMenu/VerticalMenu';
import { LeftMenuContainer } from '../../annotation/submodules/segmentations/containers/LeftMenu.container';
import { ImageSegmentations } from '../../annotation/submodules/segmentations/segmentationContextMenu/imageSegmentations.context';
import { IUserStore } from '../../user/user.store';
import { DatasetDetails } from '../DatasetDetails.context';

interface IProps {
  isLoading: boolean;
  url: string;
  lowQualityUrl: string;
  width: number;
  height: number;
}

export const DatasetDetailsImagePreviewContainer = observer((props: IProps): ReactElement => {
  const annotationUiStore = useInjection(IAnnotationUiStore.$);
  const userStore = useInjection(IUserStore.$);

  const [currentZoom, setCurrentZoom] = useState(100);
  const detailsContext = useContext(DatasetDetails);
  const imageSegmentationsContext = useContext(ImageSegmentations);

  const handleLowResImageLoaded = (): void => {
    detailsContext.store.isPreviewImageLoading = false;
  };

  const handleZoomChanged = (zoom: number): void => {
    setCurrentZoom(zoom);
  };

  const { url, width, height, isLoading, lowQualityUrl } = props;

  return (
    <div className='dataset-image-preview'>
      <LeftMenuContainer
        defaultTabOpened={VerticalMenuItem.ANNOTATED_IN_PROJECTS}
        isReadOnly={true}
        showObjectsList={false}
        isReview={false}
        projectPickerEmptyListLabel={'other_projects_description_in_dataset_zero'}
      />
      <Image
        isLeftMenuOpened={annotationUiStore.isLeftMenuOpened}
        isLoading={isLoading}
        isHighResImageLoading={url === ''}
        url={url || ''}
        lowQualityUrl={lowQualityUrl || ''}
        height={height || 0}
        width={width || 0}
        zoomLevel={currentZoom}
        minZoomLevel={annotationUiStore.minZoomLevel}
        maxZoomLevel={annotationUiStore.maxZoomLevel}
        onZoomChanged={handleZoomChanged}
        onLowResImageLoaded={handleLowResImageLoaded}
      >
        <>
          {imageSegmentationsContext.selectedProjects.map((projectId) => {
            const projectSegmentations = imageSegmentationsContext.segmentations[projectId];
            const allAttributes = projectSegmentations?.flatMap((x) => x.questions.filter((q) => isAttributeQuestion(q)).flatMap((q) => q.id)) ?? [];

            return (
              <SegmentationsLayer
                imageId={url || ''}
                key={projectId}
                readonly={true}
                editable={false}
                segmentations={imageSegmentationsContext.segmentations[projectId] || []}
                freeDrawInProgress={false}
                selectedAttributes={allAttributes}
                showAttributesHeaders={userStore.attributesNamesVisibilityPerProject.get(projectId) ?? true}
                opacityLevel={userStore.opacityLevel}
                zoomLevel={currentZoom}
                annotationTypesOptions={[]}
                hiddenSegmentations={[]}
                onSegmentationSelected={doNothing}
                onSegmentationDeselected={doNothingAsync}
                onSegmentationTabed={doNothing}
                onSegmentationOver={doNothing}
                onSegmentationOut={doNothing}
                onNextSegmentationSelected={constant(undefined)}
                onPreviousSegmentationSelected={constant(undefined)}
                reactLeafletRenderFingerprint={annotationUiStore.reactLeafletRenderFingerprint}
              />
            );
          })}
        </>

        <DrawingContainer disableCursorGuides />
      </Image>
    </div>
  );
});
